import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { grafanaQueryApiRef } from '../api/GrafanaQueryApi';
import type { LogEntry, TimeRange } from '../api/types';

interface UseLokiLogsOptions {
  service: string;
  timeRange: TimeRange;
  limit?: number;
}

export function useLokiLogs({
  service,
  timeRange,
  limit = 200,
}: UseLokiLogsOptions) {
  const api = useApi(grafanaQueryApiRef);
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const logql = useMemo(() => {
    return `{service_name=~"${service}"}`;
  }, [service]);

  const refresh = useCallback(() => {
    setLoading(true);
    api
      .queryLoki(logql, timeRange, limit)
      .then(setEntries)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [api, logql, timeRange, limit]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pods = useMemo(() => {
    const names = new Set<string>();
    for (const e of entries) {
      const pod = e.labels.k8s_pod_name ?? e.labels.pod ?? e.labels.pod_name;
      if (pod) names.add(pod);
    }
    return Array.from(names).sort();
  }, [entries]);

  return { entries, loading, error, refresh, pods };
}
